/**
 * Org Store - управление текущей организацией
 *
 * Использует OrgResolver для определения организации из:
 * 1. URL параметра ?org=xxx
 * 2. Subdomain (university.tlh.ru)
 * 3. Domain mapping
 * 4. Default (www.tlh.ru)
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { resolveLocalizedString } from '@/composables/useLocalizedString'
import { getRegistryService } from '@/services/registry'
import { getOrgResolver } from '@/shell/OrgResolver'
import type { OrgContext, OrgSource } from '@/shell/types'
import type { OrgTheme, ThemeBranding } from '@/types/theme'
import { defaultColors, defaultGlassSettings } from '@/types/theme'
import { useThemeStore } from './theme'

// Типы организации
export type OrgType = 'public' | 'university' | 'school' | 'corporate' | 'custom'

export interface OrgConfig {
  id: string
  slug: string
  type: OrgType
  domains: string[]
  theme: OrgTheme
  locale?: string
  features?: {
    tagCloud?: boolean
    presentations?: boolean
    lms?: boolean
    analytics?: boolean
    chat?: boolean
  }
}

// ============================================================================
// Дефолтные значения (fallback если API недоступен)
// ============================================================================

const defaultBranding: ThemeBranding = {
  logo: 'text:TLH',
  appName: 'Teaching & Learning Hub',
  appNameShort: 'TLH',
  tagline: 'Teach • Learn • Hub',
  // description is now handled by i18n (see locales/ru.json and locales/en.json)
}

// TLH Public - дефолтная публичная версия
export const tlhPublicConfig: OrgConfig = {
  id: 'www.tlh.ru',
  slug: 'www.tlh.ru',
  type: 'public',
  domains: ['localhost', 'tlh.ru', 'www.tlh.ru'],
  theme: {
    id: 'www.tlh.ru-theme',
    name: 'TLH Public Theme',
    preset: 'default',
    colors: defaultColors,
    glass: defaultGlassSettings,
    branding: defaultBranding,
    typography: {
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      fontSize: 'md',
    },
    shape: {
      borderRadius: 'lg',
      density: 'comfortable',
    },
  },
  features: {
    tagCloud: true,
    presentations: true,
    lms: true,
    analytics: true,
    chat: true,
  },
}

// ============================================================================
// Store
// ============================================================================

export const useOrgStore = defineStore('org', () => {
  const themeStore = useThemeStore()
  const resolver = getOrgResolver()
  const registry = getRegistryService()

  // Состояние
  const currentOrg = ref<OrgConfig>(tlhPublicConfig)
  const orgContext = ref<OrgContext | null>(null)
  const orgSource = ref<OrgSource>('default')
  const isLoading = ref(true)
  const error = ref<string | null>(null)

  // Computed
  const orgId = computed(() => currentOrg.value.id)
  const orgSlug = computed(() => currentOrg.value.slug)
  const orgType = computed(() => currentOrg.value.type)
  const branding = computed(() => currentOrg.value.theme.branding)
  const features = computed(() => currentOrg.value.features ?? {})
  const colors = computed(() => currentOrg.value.theme.colors)
  const languages = computed(() => orgContext.value?.languages ?? ['ru', 'en'])

  // Логотип может быть текстовым (text:XXX) или URL
  const logoInfo = computed(() => {
    const logo = branding.value.logo
    if (logo.startsWith('text:')) {
      return {
        type: 'text' as const,
        value: logo.replace('text:', ''),
      }
    }
    return {
      type: 'image' as const,
      value: logo,
    }
  })

  /**
   * Инициализировать организацию
   * 1. Определить slug из домена/параметра
   * 2. Загрузить данные организации от API
   * 3. Применить тему
   */
  async function initOrg(): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      // 1. Определить организацию через OrgResolver
      const resolution = resolver.resolve()
      orgSource.value = resolution.source

      console.log(`[OrgStore] Resolved org: ${resolution.slug} (source: ${resolution.source})`)

      // 2. Загрузить данные от API (или mock)
      const response = await registry.getOrgModules(resolution.slug)

      // 3. Сохранить контекст организации
      orgContext.value = response.org

      // 4. Сформировать OrgConfig из ответа API
      const orgConfig: OrgConfig = {
        id: response.org.id,
        slug: response.org.slug,
        type: response.org.type,
        domains: [window.location.hostname],
        theme: {
          id: `${response.org.slug}-theme`,
          name: `${response.org.name} Theme`,
          preset: 'default',
          colors: response.org.colors ?? defaultColors,
          glass: defaultGlassSettings,
          branding: {
            ...defaultBranding,
            ...response.org.branding,
            appName: response.org.branding.appName ?? response.org.name,
            appNameShort: response.org.branding.appNameShort ?? response.org.slug,
          },
          typography: {
            fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
            fontSize: 'md',
          },
          shape: {
            borderRadius: 'lg',
            density: 'comfortable',
          },
        },
        locale: response.org.locale,
        features: response.features.reduce<NonNullable<OrgConfig['features']>>((acc, f) => {
          // Конвертируем features в объект
          if (f.startsWith('tagcloud')) acc.tagCloud = true
          if (f.startsWith('presentations')) acc.presentations = true
          if (f.startsWith('lms')) acc.lms = true
          if (f.startsWith('analytics')) acc.analytics = true
          if (f.startsWith('chat')) acc.chat = true
          return acc
        }, {}),
      }

      setOrg(orgConfig)
    } catch (e) {
      console.error('[OrgStore] Failed to init org:', e)
      error.value = e instanceof Error ? e.message : 'Failed to load organization'

      // Fallback к дефолтной организации
      setOrg(tlhPublicConfig)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Установить текущую организацию
   */
  function setOrg(org: OrgConfig): void {
    currentOrg.value = org

    // Применить тему организации
    themeStore.setOrgTheme(org.theme)

    // Обновить favicon если указан
    if (org.theme.branding.favicon) {
      updateFavicon(org.theme.branding.favicon)
    }

    // Обновить title (используем русский язык как fallback)
    document.title = resolveLocalizedString(org.theme.branding.appName, 'ru')
  }

  /**
   * Переключить организацию (для демо/тестирования)
   */
  async function switchOrg(slug: string): Promise<void> {
    // Обновить URL параметр
    const url = new URL(window.location.href)
    url.searchParams.set('org', slug)
    window.history.replaceState({}, '', url.toString())

    // Обновить resolver config
    resolver.updateConfig({ defaultOrg: slug })

    // Перезагрузить данные организации
    await initOrg()
  }

  /**
   * Получить контекст организации для MountContext
   */
  function getOrgContext(): OrgContext {
    if (orgContext.value) {
      return orgContext.value
    }

    // Fallback из currentOrg
    return {
      id: currentOrg.value.id,
      slug: currentOrg.value.slug,
      name: currentOrg.value.theme.branding.appName,
      type: currentOrg.value.type,
      branding: currentOrg.value.theme.branding,
      colors: currentOrg.value.theme.colors,
      locale: currentOrg.value.locale,
    }
  }

  /**
   * Обновить favicon
   */
  function updateFavicon(url: string): void {
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']")
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.href = url
  }

  return {
    // Состояние
    currentOrg,
    orgContext,
    orgSource,
    isLoading,
    error,

    // Computed
    orgId,
    orgSlug,
    orgType,
    branding,
    features,
    colors,
    languages,
    logoInfo,

    // Действия
    initOrg,
    setOrg,
    switchOrg,
    getOrgContext,
  }
})
